import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type Dispatch,
  type SetStateAction,
} from 'react';
import { useWorkspaceStore } from '../../../core/store/workspaceStore';
import { usePopulationProgress, type PopulationProgressEntry } from '../../../query-editor/hooks/usePopulationProgress';
import { buildEffectiveResultSets } from '../../../query-editor/hooks/buildEffectiveResultSets';
import { hasUserPrintMessages } from '../../utils/plaintextResultsUtils';
import { getResultCacheMetadata } from '../../services/resultCacheApi';
import { toast } from '../../../../../components/ui/toaster';
import { useChartSample } from './useChartSample';
import type { QueryTabContent, PinnedResultEntry, QueryMessage } from '../../../core/types/workspaceTypes';
import type { ResultCacheCapabilities, QueryResultSet } from '../../../../../types/queryExecution';
import type { ChartConfig } from '../../../charts/types/chartTypes';
import { decideRenderHintAction } from '../../../query-editor/utils/renderHintDecision';
import { useAnalytics } from '../../../../../core/analytics';
import type { DatabaseEngineId } from '../../../../../core/engines/engineTypes';

/**
 * The results-bearing slice of a tab's content that the shared results panel
 * needs (a `Pick` of `QueryTabContent`).
 */
export type ResultsBearingContent = Pick<QueryTabContent,
  'resultSets' | 'messages' | 'messagesRunStart' | 'messagesRunEnd' | 'stats' | 'executionPlan' |
  'chartConfigs' | 'pinnedResultSets' | 'resultSetCacheUris' | 'cacheGeneration' | 'results' | 'columns'>;

/**
 * The messages of the LATEST run only.
 *
 * `content.messages` is a log that accumulates across runs in grid mode (the
 * Messages tab wants the history). The Output view and the result-set strip's
 * Output tab describe ONE execution — they interleave `[Result N]` markers
 * with the lines a script printed — so they take this instead, or a second run
 * of the same script repeats the first run's PRINTs and spends the markers on
 * its row counts. `undefined` (a tab from before the field existed, or
 * plaintext mode, which resets the log per run) means the whole array is the
 * current run.
 */
export function currentRunMessages(
  content:
    | { messages?: QueryMessage[]; messagesRunStart?: number; messagesRunEnd?: number }
    | undefined,
): QueryMessage[] | undefined {
  const messages = content?.messages;
  if (!messages) return messages;
  const start = content?.messagesRunStart ?? 0;
  // `messagesRunEnd` is set only when something appended to the log after the
  // run without belonging to it (an estimated-plan capture). Without it the
  // window runs to the end, which is what an ordinary run leaves.
  //
  // Clamped to at least `start`: the two are written by different call sites
  // (the execution and the plan capture), so a stale end from a capture that
  // resolved after a newer run began would otherwise invert the window. An
  // inverted window silently yields nothing, which reads as "this run printed
  // nothing" — the one failure mode worth making structurally impossible.
  const end = Math.max(start, content?.messagesRunEnd ?? messages.length);
  return start > 0 || end < messages.length ? messages.slice(start, end) : messages;
}

export type ResultsPanelState = {
  activeTab: string;
  setActiveTab: Dispatch<SetStateAction<string>>;
  collapsed: boolean;
  setCollapsed: Dispatch<SetStateAction<boolean>>;
  zoomLevel: number;
  setZoomLevel: Dispatch<SetStateAction<number>>;
  scrollRow: number;
  activeResultSetIndex: number;
  activeResultSet: QueryResultSet | undefined;
  effectiveResultSets: QueryResultSet[] | undefined;
  populationEntry: PopulationProgressEntry | undefined;
  activeCacheUri: string | undefined;
  activeRowCount: number | undefined;
  activeCapabilities: ResultCacheCapabilities | undefined;
  isWindowed: boolean;
  /** Advances on grid sort/filter mutations — cache-window consumers (chart sample, Map view) re-key on it. */
  sourceGeneration: number;
  chartResultSet: QueryResultSet | undefined;
  activeChartConfig: ChartConfig | undefined;
  /** Kusto only: the active chart was auto-opened from a `| render` hint. */
  chartFromRenderHint: boolean;
  chartBufferRows: Record<string, unknown>[];
  handleTabChange: (next: string) => void;
  handleChartConfigChange: (config: ChartConfig | undefined) => void;
  handleActiveResultSetChange: (index: number) => void;
  handleScrollPositionChange: (firstVisibleRow: number) => void;
  handleSourceGenerationChange: (gen: number) => void;
  handlePin: () => Promise<void>;
  handleClosePinnedTab: (pinnedId: string) => void;
};

/**
 * Owns all pane-scoped state for the shared results panel: the active tab,
 * collapse/zoom, active result-set index + derivations, the two result-cache
 * IPC effects, and the pin/unpin handlers. Called by the HOST so existing
 * host-side writers of `activeTab`/`collapsed` (execution-success tab switch,
 * plan capture, MCP commands, Ctrl+J) keep their exact code shape by
 * destructuring this hook's return under the original local names.
 *
 * `getPinnedQueryText` supplies the live editor buffer for a pin snapshot's
 * `query` field (the QE reads Monaco's truth via `queryRef.current`, which this
 * hook can't reach directly).
 */
export function useResultsPanelState(params: {
  tabId: string;
  content: ResultsBearingContent | undefined;
  isExecuting: boolean;
  getPinnedQueryText?: () => string;
  /** Host engine — drives the kusto `| render` auto-chart effect. Optional so
   *  non-kusto hosts (VQE) can omit it; the effect is a no-op unless 'kusto'. */
  engineId?: DatabaseEngineId;
}): ResultsPanelState {
  const { tabId, content, isExecuting, getPinnedQueryText, engineId } = params;
  const updateTabContent = useWorkspaceStore((state) => state.updateTabContent);
  const { track } = useAnalytics();

  const [activeTab, setActiveTab] = useState('results');
  // Synchronous mirror of "the current result auto-opened a `| render` chart"
  // (kusto). Read by the populating effect below so a streaming result doesn't
  // yank the user off the auto-opened Chart tab back to the grid. A ref (not
  // state) so the render-hint effect further down can set it before this effect
  // re-runs.
  const renderChartActiveRef = useRef(false);
  // Track which result set is active — synced from ResultsGrid via onActiveResultSetChange
  const [activeResultSetIndex, setActiveResultSetIndex] = useState(0);
  const [zoomLevel, setZoomLevel] = useState(1);
  // Current scroll position (first visible row) for status bar display
  const [scrollRow, setScrollRow] = useState(0);
  const [bottomPaneCollapsed, setBottomPaneCollapsed] = useState(false);

  // Population progress — tracks rows arriving from Electron during execution
  const populationProgress = usePopulationProgress(tabId, isExecuting, content?.cacheGeneration);

  // When rows start arriving, switch to the Results tab so the user can watch them populate
  const isAnyResultPopulating = populationProgress
    ? Object.values(populationProgress).some((p) => p?.state === 'populating')
    : false;
  useEffect(() => {
    if (isAnyResultPopulating) {
      setActiveTab((prev) => {
        if (prev === 'results' || prev.startsWith('pinned-')) return prev;
        // Keep an auto-opened `| render` chart (kusto) — don't yank to the grid.
        if (prev === 'chart' && renderChartActiveRef.current) return prev;
        return 'results';
      });
    }
  }, [isAnyResultPopulating]);

  const handleTabChange = useCallback((next: string) => {
    setActiveTab(next);
    setBottomPaneCollapsed((prev) => (prev ? false : prev));
  }, []);

  // Handle chart config changes
  const handleChartConfigChange = useCallback((config: ChartConfig | undefined) => {
    const existingConfigs = content?.chartConfigs || {};
    const newConfigs = config
      ? { ...existingConfigs, [activeResultSetIndex]: config }
      : Object.fromEntries(
          Object.entries(existingConfigs).filter(([key]) => Number(key) !== activeResultSetIndex)
        );
    updateTabContent(tabId, { chartConfigs: newConfigs });
  }, [content?.chartConfigs, activeResultSetIndex, tabId, updateTabContent]);

  // Get active result set and its chart config
  const activeResultSet = content?.resultSets?.[activeResultSetIndex];
  const activeChartConfig = content?.chartConfigs?.[activeResultSetIndex];

  // Kusto only: a query ending in `| render <type>` auto-opens the Chart tab
  // with a prefilled config. Keyed on `activeResultSet` IDENTITY so it fires
  // once per NEW result set (ref-guarded), not on every unrelated re-render.
  const [chartFromRenderHint, setChartFromRenderHint] = useState(false);
  const autoChartedResultRef = useRef<QueryResultSet | undefined>(undefined);
  // The exact ChartConfig object last auto-applied from a `| render` hint —
  // lets decideRenderHintAction tell our own auto config apart from a
  // user-authored one (the store keeps object identity; the chart panel's
  // onChange always emits a NEW object, so a differing ref ⇒ user-edited).
  const autoAppliedChartConfigRef = useRef<ChartConfig | undefined>(undefined);
  useEffect(() => {
    if (engineId !== 'kusto') return;
    if (!activeResultSet || activeResultSet === autoChartedResultRef.current) return;
    autoChartedResultRef.current = activeResultSet;
    const decision = decideRenderHintAction(
      activeResultSet.renderHint,
      activeResultSet.columns,
      activeChartConfig,
      autoAppliedChartConfigRef.current,
    );
    if (decision.type === 'apply') {
      handleChartConfigChange(decision.config);
      autoAppliedChartConfigRef.current = decision.config;
      renderChartActiveRef.current = true;
      setActiveTab('chart');
      setChartFromRenderHint(true);
      track('feature:kusto_render_chart', { type: decision.config.type });
      return;
    }
    // 'respect-user' | 'skip' | 'clear': never keep the streaming-race guard on.
    renderChartActiveRef.current = false;
    if (decision.type === 'clear') {
      setChartFromRenderHint(false);
      if (decision.clearStoredConfig) {
        handleChartConfigChange(undefined);
        autoAppliedChartConfigRef.current = undefined;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeResultSet, engineId, activeChartConfig, handleChartConfigChange, track]);

  // Derive per-result-set cache URI and row count for the active result set.
  // During population, use progress events so the grid can show partial data.
  const populationEntry = populationProgress?.[activeResultSetIndex];
  // During inline-mode population the renderer holds rows directly (no backend source yet),
  // so we must NOT route the grid through the windowed cache URI — there's nothing to fetch.
  // Only adopt the populating URI when the result set is in windowed mode.
  const populatingCacheUri = populationEntry?.state === 'populating' && populationEntry?.mode === 'windowed'
    ? populationEntry.uri
    : undefined;
  const activeCacheUri = content?.resultSetCacheUris?.[activeResultSetIndex] ?? populatingCacheUri;
  const activeCacheUriRef = useRef(activeCacheUri);
  activeCacheUriRef.current = activeCacheUri;
  const cacheGenerationRef = useRef(content?.cacheGeneration);
  cacheGenerationRef.current = content?.cacheGeneration;
  const activeRowCount = activeResultSet?.rowCount
    ?? (populationEntry?.state === 'populating' ? populationEntry.loadedRowCount : undefined);

  // During population, provide temporary result sets with columns from progress events
  // so the grid can render headers while rows stream in.
  const effectiveResultSets = useMemo(
    () => buildEffectiveResultSets(content?.resultSets, populationProgress),
    [content?.resultSets, populationProgress],
  );

  const isWindowed = Boolean(activeCacheUri && activeRowCount !== undefined);
  // Track source generation from ResultsGrid (advances on sort/filter mutations)
  const [sourceGeneration, setSourceGeneration] = useState(0);
  const handleSourceGenerationChange = useCallback((gen: number) => {
    setSourceGeneration(gen);
  }, []);
  // In windowed mode, fetch a stable sample of rows from cache for chart rendering.
  const chartBufferRows = useChartSample({
    isWindowed,
    activeCacheUri,
    activeRowCount,
    cacheGeneration: content?.cacheGeneration,
    sourceGeneration,
  });

  // Fetch capabilities from the result source for the active windowed result set
  const [activeCapabilities, setActiveCapabilities] = useState<ResultCacheCapabilities | undefined>(undefined);
  useEffect(() => {
    if (!isWindowed || !activeCacheUri) {
      setActiveCapabilities(undefined);
      return;
    }
    const uriAtFetchTime = activeCacheUri;
    const genAtFetchTime = content?.cacheGeneration;
    getResultCacheMetadata({ uri: uriAtFetchTime })
      .then((meta) => {
        if (activeCacheUriRef.current !== uriAtFetchTime) return;
        if (cacheGenerationRef.current !== genAtFetchTime) return;
        setActiveCapabilities(meta.capabilities);
      })
      .catch(() => setActiveCapabilities(undefined));

  }, [isWindowed, activeCacheUri, content?.cacheGeneration]);

  const chartResultSet = useMemo(() => {
    if (!isWindowed || !activeResultSet) return activeResultSet;
    if (chartBufferRows.length === 0) return activeResultSet;
    return { ...activeResultSet, rows: chartBufferRows };
  }, [isWindowed, activeResultSet, chartBufferRows]);

  // Sync active result set index from ResultsGrid
  const handleActiveResultSetChange = useCallback((index: number) => {
    setActiveResultSetIndex(index);
  }, []);

  const handleScrollPositionChange = useCallback((firstVisibleRow: number) => {
    setScrollRow(firstVisibleRow);
  }, []);

  // Pin results: snapshot the entire execution (every result set + the Output messages)
  // as a single pinned tab. One pin click captures one execution.
  const handlePin = useCallback(async () => {
    const liveResultSets = content?.resultSets ?? [];
    // The pin captures ONE execution, so its Output tab must show that run's
    // lines — not every run the tab has accumulated.
    const liveMessages = currentRunMessages({
      messages: content?.messages,
      messagesRunStart: content?.messagesRunStart,
      messagesRunEnd: content?.messagesRunEnd,
    });
    const hasOutput = hasUserPrintMessages(liveMessages);
    if (liveResultSets.length === 0 && !hasOutput) return;
    const newId = crypto.randomUUID();
    const existing = content?.pinnedResultSets ?? [];
    const liveCacheUris = content?.resultSetCacheUris ?? [];

    const snapshotResultSets: QueryResultSet[] = new Array(liveResultSets.length);
    const sourceUris: (string | undefined)[] = new Array(liveResultSets.length);
    const sourceRowCounts: (number | undefined)[] = new Array(liveResultSets.length);
    const capabilitiesByIndex: (ResultCacheCapabilities | undefined)[] = new Array(liveResultSets.length);

    let cloneFailed = false;
    await Promise.all(liveResultSets.map(async (rs, index) => {
      const liveUri = liveCacheUris[index];
      if (!liveUri) {
        // Inline result set — no backing source, snapshot the rows directly.
        snapshotResultSets[index] = structuredClone(rs);
        return;
      }
      const pinUri = `result-cache://pinned-${newId}/rs/${index}`;
      const result = await window.electronAPI?.resultCache.cloneForPin({
        existingUri: liveUri,
        newUri: pinUri,
      });
      if (!result) {
        cloneFailed = true;
        return;
      }
      sourceUris[index] = pinUri;
      sourceRowCounts[index] = result.rowCount;
      snapshotResultSets[index] = { columns: result.columns, rows: [], rowCount: result.rowCount };

      if (index === activeResultSetIndex && activeCapabilities) {
        capabilitiesByIndex[index] = activeCapabilities;
        return;
      }
      try {
        const meta = await getResultCacheMetadata({ uri: pinUri });
        capabilitiesByIndex[index] = meta.capabilities;
      } catch {
        capabilitiesByIndex[index] = { canSort: false, canFilter: false, canEdit: false, supportsAccurateCount: true };
      }
    }));

    if (cloneFailed) {
      // Best-effort cleanup of any clones that did succeed before bailing out.
      for (const u of sourceUris) {
        if (u) window.electronAPI?.resultCache?.discard({ uri: u }).catch(() => {});
      }
      toast.error({
        title: 'Failed to pin results',
        description: 'The result source may have been disposed. Re-run the query and try again.',
      });
      return;
    }

    const hasAnyWindowed = sourceUris.some((u) => u != null);
    const entry: PinnedResultEntry = {
      id: newId,
      resultSets: snapshotResultSets,
      messages: liveMessages,
      pinnedAt: new Date().toISOString(),
      query: getPinnedQueryText?.() ?? '',
      sourceUris: hasAnyWindowed ? sourceUris : undefined,
      sourceRowCounts: hasAnyWindowed ? sourceRowCounts : undefined,
      capabilitiesByIndex: hasAnyWindowed ? capabilitiesByIndex : undefined,
    };

    updateTabContent(tabId, {
      pinnedResultSets: [...existing, entry],
    });
    setActiveTab(`pinned-${newId}`);
  }, [
    content?.resultSets,
    content?.messages,
    // `currentRunMessages` reads both halves of the slice.
    content?.messagesRunStart,
    content?.messagesRunEnd,
    content?.pinnedResultSets,
    content?.resultSetCacheUris,
    tabId,
    updateTabContent,
    activeResultSetIndex,
    activeCapabilities,
    getPinnedQueryText,
  ]);

  // Close a pinned tab by id
  const handleClosePinnedTab = useCallback((pinnedId: string) => {
    const existing = content?.pinnedResultSets ?? [];
    const pinned = existing.find((p) => p.id === pinnedId);
    for (const uri of pinned?.sourceUris ?? []) {
      if (uri) window.electronAPI?.resultCache.discard({ uri }).catch(() => {});
    }
    const updated = existing.filter((p) => p.id !== pinnedId);
    updateTabContent(tabId, { pinnedResultSets: updated.length > 0 ? updated : undefined });
    setActiveTab((prev) => prev === `pinned-${pinnedId}` ? 'results' : prev);
  }, [content?.pinnedResultSets, tabId, updateTabContent]);

  return {
    activeTab,
    setActiveTab,
    collapsed: bottomPaneCollapsed,
    setCollapsed: setBottomPaneCollapsed,
    zoomLevel,
    setZoomLevel,
    scrollRow,
    activeResultSetIndex,
    activeResultSet,
    effectiveResultSets,
    populationEntry,
    activeCacheUri,
    activeRowCount,
    activeCapabilities,
    isWindowed,
    sourceGeneration,
    chartResultSet,
    activeChartConfig,
    chartFromRenderHint,
    chartBufferRows,
    handleTabChange,
    handleChartConfigChange,
    handleActiveResultSetChange,
    handleScrollPositionChange,
    handleSourceGenerationChange,
    handlePin,
    handleClosePinnedTab,
  };
}
